import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface AuthGuardProps {
  children: ReactNode;
  requireTeam?: boolean;
  requireAdmin?: boolean;
}

export const AuthGuard: React.FC<AuthGuardProps> = ({ 
  children, 
  requireTeam = true,
  requireAdmin = false 
}) => {
  const { isAuthenticated, isLoading, user, team } = useAuth();
  const location = useLocation();

  console.log('🔍 AuthGuard check:', {
    isAuthenticated,
    isLoading,
    hasUser: !!user,
    hasTeam: !!team,
    requireTeam,
    currentPath: location.pathname,
    timestamp: new Date().toISOString()
  });

  // Show loading spinner while checking authentication
  if (isLoading) {
    console.log('🔄 AuthGuard: Showing loading spinner');
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    console.log('🚫 AuthGuard: Redirecting to login - not authenticated or no user');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect to team selection if team is required but not set
  if (requireTeam && !team) {
    console.log('🚫 AuthGuard: Redirecting to team selection - no team');
    return <Navigate to="/team-selection" replace />;
  }

  // Check admin permission if required
  if (requireAdmin && user?.role !== 'admin' && team?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            アクセス権限がありません
          </h2>
          <p className="text-gray-600">
            この機能を利用するには管理者権限が必要です。
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

// Convenience components for specific use cases
export const PublicRoute: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  console.log('🔍 PublicRoute check:', {
    isAuthenticated,
    isLoading,
    timestamp: new Date().toISOString()
  });
  
  if (isLoading) {
    console.log('🔄 PublicRoute: Showing loading spinner');
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Redirect authenticated users to dashboard
  if (isAuthenticated) {
    console.log('✅ PublicRoute: Redirecting authenticated user to dashboard');
    return <Navigate to="/dashboard" replace />;
  }

  console.log('👤 PublicRoute: Showing public content');
  return <>{children}</>;
};

export const AdminRoute: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <AuthGuard requireTeam={true} requireAdmin={true}>
      {children}
    </AuthGuard>
  );
};